import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { createApi } from '@reduxjs/toolkit/query/react'
import { message } from 'antd'

import type { RootState } from '@web/app/store'
import { selectActiveServerUrl } from '@web/entities/server/model/server.slice'
import { setLogout } from '@web/entities/user/model/auth.slice'
import { ApiTag } from '@web/shared/constants/api.constants'
import { API_TAG } from '@web/shared/constants/config.constant'
import {
  rawBaseQuery,
  refreshAuthSession
} from '@web/shared/infrastructure/auth-session'
import { showApiError } from '@web/shared/utils/show-internal-server-error.util'

const logoutState = { isLoggingOut: false }
const MSG_DURATION = 3000

// если логаут уже идёт, повторно не активируем
const handleUnauthorized = (
  objFlag: typeof logoutState,
  duration: typeof MSG_DURATION
) => {
  if (objFlag.isLoggingOut) return false

  objFlag.isLoggingOut = true

  setTimeout(() => {
    objFlag.isLoggingOut = false
  }, duration)

  return true
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, store, extraOptions) => {
  const state = store.getState() as RootState
  const host = selectActiveServerUrl(state)
  const baseUrl = `${host}/${API_TAG}`

  const preparedArgs =
    typeof args === 'string'
      ? { url: `${baseUrl}${args}` }
      : { ...args, url: `${baseUrl}${args.url}` }

  let result = await rawBaseQuery(preparedArgs, store, extraOptions)

  // logout вызывается и при уже недействительном refreshToken — ошибку не показываем,
  // т.к. локальный выход из аккаунта в любом случае должен пройти успешно
  const isLogoutRequest = preparedArgs.url.endsWith('/auth/logout')

  if (result.error?.status !== 401 && result.error?.status !== 404) {
    if (result.error?.status && !isLogoutRequest) showApiError(result.error)

    return result
  }

  // если refreshToken отсутствует — логаут
  const hasRefresh = (store.getState() as RootState).auth.refreshToken
  if (!hasRefresh && handleUnauthorized(logoutState, MSG_DURATION)) {
    message.warning(
      'Сессия истекла. Пожалуйста, войдите снова.',
      MSG_DURATION / 1000
    )

    store.dispatch(setLogout())

    return result
  }

  const tokens = await refreshAuthSession(store, extraOptions)

  if (!tokens && handleUnauthorized(logoutState, MSG_DURATION)) {
    message.warning(
      'Сессия истекла. Пожалуйста, войдите снова.',
      MSG_DURATION / 1000
    )

    return result
  }

  // повторяем запрос
  result = await rawBaseQuery(preparedArgs, store, extraOptions)

  if (result.error?.status) {
    if (
      result.error.status !== 401 &&
      result.error.status !== 404 &&
      !isLogoutRequest
    ) {
      showApiError(result.error)
    }
  }

  return result
}

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: true,
  tagTypes: [
    ApiTag.Connections,
    ApiTag.Generators,
    ApiTag.Machines,
    ApiTag.Machine,
    ApiTag.MeasuringPoints,
    ApiTag.MeasuringPoint,
    ApiTag.Measurements,
    ApiTag.Results,
    ApiTag.Plans,
    ApiTag.Mnemo,
    ApiTag.Info,
    ApiTag.Images,
    ApiTag.Kinematic,
    ApiTag.ServerName,
    ApiTag.GetAllNodes,
    ApiTag.GetAllDevices,
    ApiTag.Vp3711,
    ApiTag.EquipmentGroup,
    ApiTag.EquipmentGroups,
    ApiTag.DeviceGroup,
    ApiTag.DeviceGroups,
    ApiTag.WorkMode,
    ApiTag.Threshold,
    ApiTag.Plan,
    ApiTag.Users,
    ApiTag.Vp3701Device,
    ApiTag.Util,
    ApiTag.MeasurementsTree,
    ApiTag.Controllers,
    ApiTag.Profile
  ],
  endpoints: () => ({})
})
